import type { CalculationRequest, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, type ChartConfig } from '@/components/ui/shadcn/chart';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { Input } from '@/components/ui/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useSettings } from '@/contexts/SettingsContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency } from '@/lib/utils';
import type { Company, Lead, Project, ProjectTask, User as UserType, WorkOrder } from '@/types';
import { Link } from '@inertiajs/react';
import { endOfWeek, format, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import {
    ArrowDownRight,
    ArrowUpRight,
    Building2,
    Calculator,
    CalendarCheck,
    CalendarClock,
    CalendarDays,
    CheckSquare,
    ClipboardList,
    Contact,
    FolderOpen,
    Gauge,
    Hourglass,
    Info,
    Mail,
    MapPin,
    Phone,
    Search,
    TrendingUp,
    User as UserIcon,
    UserRound,
    Users,
    Wrench,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Rectangle, XAxis, YAxis } from 'recharts';

/**
 * The overview ("Details") tab is not one view but four: what a sales manager
 * needs to see on a colleague's profile has almost nothing in common with what
 * a service worker needs. The shell is shared — headline metrics, a main panel,
 * a summary rail, the identity cards, a work summary strip — and each role
 * fills it with its own figures.
 *
 * Service Manager and Service Supervisor deliberately resolve to the same view:
 * they run the same board, only the scope differs.
 */
export type EmployeeRoleView = 'sales' | 'service_manager' | 'service_worker' | 'project_manager' | 'engineer' | 'generic';

export function resolveEmployeeRoleView(role?: string | null): EmployeeRoleView {
    switch (role) {
        case 'Sales Manager':
            return 'sales';
        case 'Engineer':
            return 'engineer';
        case 'Service Manager':
        case 'Service Supervisor':
            return 'service_manager';
        case 'Service Worker':
            return 'service_worker';
        case 'Project Manager':
            return 'project_manager';
        default:
            return 'generic';
    }
}

/** Extra figures the page cannot derive from the records it already holds. */
export interface EmployeeStats {
    /** Contacts registered at the companies this employee is assigned to. */
    contacts: number;
    /** Distinct colleagues holding a task on a project this employee owns. */
    team_size: number;
}

export interface EmployeeReport {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    position?: string | null;
    role?: string | null;
}

type CompanyWithMeta = Company & { total_people?: number };

interface EmployeeOverviewProps {
    user: UserType;
    roleView: EmployeeRoleView;
    companies: CompanyWithMeta[];
    leads: Lead[];
    projects: Project[];
    tasks: ProjectTask[];
    workOrders: WorkOrder[];
    /** Calculations sitting with this employee — only engineers are ever assigned one. */
    calculationRequests: CalculationRequest[];
    /** The admin-managed status catalogue those calculations are reported against. */
    calculationStatuses: CalculationRequestOptions['statuses'];
    stats: EmployeeStats;
    reports: EmployeeReport[];
    manager?: { id: string; first_name?: string | null; last_name?: string | null } | null;
}

/** Axis ticks: € totals would otherwise run past the plot area. */
const compactNumber = (value: number): string => new Intl.NumberFormat(undefined, { notation: 'compact' }).format(value);

/** Where a record sits in its lifecycle, regardless of the configured status catalogue. */
type LifecycleState = 'open' | 'done' | 'cancelled';

const toDate = (value?: string | null): Date | null => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Statuses are admin-configurable, so nothing may hard-code the catalogue.
 * Matching on the stored value keeps renamed or added statuses classified.
 */
const lifecycleOf = (status?: string | null): LifecycleState => {
    const value = (status ?? '').toLowerCase();

    if (value.includes('cancel') || value.includes('lost') || value.includes('reject')) {
        return 'cancelled';
    }

    if (value.includes('complet') || value.includes('closed') || value.includes('won') || value.includes('done') || value.includes('finish')) {
        return 'done';
    }

    return 'open';
};

/** Open work whose due date has passed — the one figure every service view leads with. */
const isOverdue = (status?: string | null, due?: string | null): boolean => {
    const date = toDate(due);

    return Boolean(date) && lifecycleOf(status) === 'open' && format(date as Date, 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd');
};

const isToday = (value?: string | null): boolean => {
    const date = toDate(value);

    return Boolean(date) && format(date as Date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
};

const isThisWeek = (value?: string | null): boolean => {
    const date = toDate(value);
    if (!date) {
        return false;
    }

    const now = new Date();

    return date >= startOfWeek(now, { weekStartsOn: 1 }) && date <= endOfWeek(now, { weekStartsOn: 1 });
};

/**
 * When a work order landed on this employee's plate. Assignments carry the
 * timestamp; the work order's own creation date stands in for rows assigned
 * before the column was filled.
 */
/** WorkOrder does not append `id`, so the uuid is what actually arrives. */
const workOrderIdOf = (workOrder: WorkOrder): string => String(workOrder.id ?? workOrder.uuid ?? '');

const assignedDateFor = (workOrder: WorkOrder, userId: string): string | null => {
    const assignment = (workOrder.assignments ?? []).find((entry) => entry.user_id === userId || entry.user?.id === userId);

    return (assignment?.assigned_at as string | undefined) ?? (workOrder.created_at as string | undefined) ?? null;
};

/**
 * Where a calculation stands.
 *
 * `stage` is resolved server-side against the settings catalogue, and any status
 * missing from it falls back to "working" — so a catalogue that has drifted from
 * the stored values would report every calculation as in progress. The status
 * value itself is therefore read first, with the stage as the tie-breaker for
 * statuses an admin has renamed beyond recognition.
 */
const calculationStateOf = (request: CalculationRequest): LifecycleState => {
    const byStatus = lifecycleOf(request.status);

    if (byStatus !== 'open') {
        return byStatus;
    }

    if (request.stage === 'done') {
        return 'done';
    }

    if (request.stage === 'closed') {
        return 'cancelled';
    }

    return 'open';
};

/** Turns a raw status value into something readable when it is not in the catalogue. */
const humaniseStatus = (value: string): string =>
    value
        .split(/[_-]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

/**
 * When a calculation reached the engineer: acceptance is the moment it became
 * theirs, with submission and creation as fallbacks for rows that skipped it.
 */
const calculationDateOf = (request: CalculationRequest): string | null => request.accepted_at ?? request.submitted_at ?? request.created_at ?? null;

/**
 * Month-over-month change, measured on whichever date the role reports against
 * — signing for sales, assignment for a service worker, creation otherwise.
 * Returns undefined when the previous month is empty: a "+100%" off a zero base
 * says nothing.
 */
const monthlyDelta = <T,>(records: T[], dateOf: (record: T) => string | null | undefined): number | undefined => {
    const now = new Date();
    const thisMonth = format(startOfMonth(now), 'yyyy-MM');
    const lastMonth = format(startOfMonth(subMonths(now, 1)), 'yyyy-MM');

    let current = 0;
    let previous = 0;

    records.forEach((record) => {
        const date = toDate(dateOf(record));
        if (!date) {
            return;
        }

        const bucket = format(date, 'yyyy-MM');
        if (bucket === thisMonth) {
            current += 1;
        } else if (bucket === lastMonth) {
            previous += 1;
        }
    });

    if (previous === 0) {
        return undefined;
    }

    return Math.round(((current - previous) / previous) * 100);
};

/** The stacks a period can hold; `null` means the pointer is on neither column. */
type TrendStack = 'primary' | 'second' | null;

interface TrendTooltipProps {
    active?: boolean;
    payload?: Array<{ dataKey?: string | number; value?: number | string; color?: string }>;
    label?: string;
    config: ChartConfig;
    /** Which column the pointer is over — its series are the only ones listed. */
    stack: TrendStack;
    /** Sales stacks money; every other view stacks record counts. */
    isMoney: boolean;
    totalLabel: string;
}

/**
 * Recharts' shared tooltip lists every series in the period, which on the sales
 * chart means seven rows for two columns. This narrows the list to the column
 * under the pointer and totals it, so hovering a bar answers one question.
 */
function TrendTooltip({ active, payload, label, config, stack, isMoney, totalLabel }: TrendTooltipProps) {
    if (!active || !payload?.length) {
        return null;
    }

    const belongsToStack = (key: string): boolean => {
        if (stack === null) {
            return true;
        }

        const isSecondSeries = key.startsWith('second') && key !== 'secondary';

        return stack === 'second' ? isSecondSeries : !isSecondSeries && key !== 'secondary';
    };

    const rows = payload.filter((item) => belongsToStack(String(item.dataKey ?? '')));

    if (rows.length === 0) {
        return null;
    }

    const format = (key: string, value: number) => (isMoney && key !== 'secondary' ? formatCurrency(value) : value.toLocaleString());
    const total = rows.reduce((sum, item) => sum + Number(item.value ?? 0), 0);

    return (
        <div className="bg-background grid min-w-[13em] gap-[0.4em] rounded-lg border px-[0.8em] py-[0.5em] text-xs shadow-md">
            <div className="font-medium">{label}</div>
            {rows.map((item) => {
                const key = String(item.dataKey ?? '');
                const series = config[key as keyof typeof config];

                return (
                    <div key={key} className="flex items-center gap-[0.5em]">
                        <span
                            className="size-[0.85em] shrink-0 rounded-[0.15em]"
                            style={{ backgroundColor: item.color ?? (series as { color?: string })?.color }}
                        />
                        <span className="text-muted-foreground min-w-0 flex-1">{series?.label ?? key}</span>
                        <span className="text-foreground font-medium tabular-nums">{format(key, Number(item.value ?? 0))}</span>
                    </div>
                );
            })}
            {stack !== null && rows.length > 1 && (
                <div className="flex items-center gap-[0.5em] border-t pt-[0.4em] font-medium">
                    <span className="min-w-0 flex-1">{totalLabel}</span>
                    <span className="tabular-nums">{format('total', total)}</span>
                </div>
            )}
        </div>
    );
}

/** The series a stack is built from, bottom to top. */
const LIFECYCLE_KEYS = ['open', 'done', 'cancelled'] as const;
const SECOND_STACK_KEYS = ['secondOpen', 'secondDone', 'secondCancelled'] as const;

/**
 * Rounds the top of a stacked bar once, not once per segment.
 *
 * Recharts applies `radius` to every rectangle it draws, so a stack of three
 * came out with a rounded seam between each pair. This gives the corners to
 * whichever series is the topmost non-zero one for that period — which, when a
 * period has a single non-zero series, is that series, so nothing changes.
 */
const stackTopRadius = (stackKeys: readonly string[], key: string) => (props: unknown) => {
    const rectangleProps = (props ?? {}) as { payload?: Record<string, number | undefined> };
    const payload = rectangleProps.payload ?? {};
    const topKey = [...stackKeys].reverse().find((candidate) => Number(payload[candidate] ?? 0) > 0);

    return <Rectangle {...(props as object)} radius={topKey === key ? [4, 4, 0, 0] : 0} />;
};

interface MetricDefinition {
    key: string;
    icon: ReactNode;
    tone: string;
    label: string;
    value: string | number;
    subtitle?: string;
    delta?: number;
}

/** One headline tile: tinted icon, label, figure, and an optional trend line. */
function MetricCard({ icon, tone, label, value, subtitle, delta, deltaLabel }: MetricDefinition & { deltaLabel: string }) {
    return (
        <Card className="gap-0 py-0">
            <CardContent className="flex items-start gap-3 p-4">
                <span className={cn('inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="mt-1 text-3xl leading-none font-semibold tabular-nums">{value}</div>
                    {subtitle && <div className="text-muted-foreground mt-1.5 text-xs">{subtitle}</div>}
                    {delta !== undefined && (
                        <div className="mt-3 flex items-center gap-1.5 text-xs">
                            <span
                                className={cn(
                                    'inline-flex items-center gap-0.5 font-medium',
                                    delta >= 0 ? 'text-success-subtle-foreground' : 'text-destructive',
                                )}
                            >
                                {delta >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                                {Math.abs(delta)}%
                            </span>
                            <span className="text-muted-foreground">{deltaLabel}</span>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

/** A labelled row in the summary rail and the identity cards. */
function SummaryRow({ icon, label, children }: { icon?: ReactNode; label: string; children?: ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 py-2.5">
            <div className="text-muted-foreground flex min-w-0 items-center gap-2 text-sm">
                {icon}
                <span className="truncate">{label}</span>
            </div>
            <div className="text-right text-sm font-medium break-words">{children ?? <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}

interface StatusGroup {
    title: string;
    items: Array<{ label: string; count: number; dot: string; /** Unit or logged time shown beside the figure. */ hint?: string }>;
}

export default function EmployeeOverview({
    user,
    roleView,
    companies,
    leads,
    projects,
    tasks,
    workOrders,
    calculationRequests,
    calculationStatuses,
    stats,
    reports,
    manager,
}: EmployeeOverviewProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();

    const [trendMonths, setTrendMonths] = useState<'6' | '12'>('6');
    // Which column the pointer is over, so the tooltip can report just that one.
    const [hoveredStack, setHoveredStack] = useState<TrendStack>(null);
    const [trendGrouping, setTrendGrouping] = useState<'month' | 'quarter'>('month');

    const isService = roleView === 'service_manager' || roleView === 'service_worker';
    // Sales sees two columns per period: projects beside the leads behind them.
    // Sales pairs projects with the leads behind them; a project manager pairs
    // projects with the tasks on them.
    const hasSecondStack = roleView === 'sales' || roleView === 'project_manager';
    // The project manager's companion measure is now a column, not a line.
    const hasLineSeries = roleView !== 'project_manager';

    const openTasks = useMemo(() => tasks.filter((task) => lifecycleOf(task.status) === 'open'), [tasks]);
    const openWorkOrders = useMemo(() => workOrders.filter((workOrder) => lifecycleOf(workOrder.status) === 'open'), [workOrders]);
    const activeProjects = useMemo(() => projects.filter((project) => lifecycleOf(project.status) === 'open'), [projects]);
    const activeLeads = useMemo(() => leads.filter((lead) => lifecycleOf(lead.status) === 'open'), [leads]);

    /**
     * Calculations are classified by their lifecycle *stage*, never by the status
     * value — statuses are admin-managed and may be renamed at will.
     */
    const calculationStats = useMemo(() => {
        const summary = {
            /** Everything ever assigned to them — the "how many do they get" figure. */
            total: calculationRequests.length,
            open: 0,
            queued: 0,
            working: 0,
            paused: 0,
            done: 0,
            closed: 0,
            overdue: 0,
            hours: 0,
            hoursDone: 0,
            turnaroundTotal: 0,
            turnaroundCount: 0,
            queueWaitTotal: 0,
            queueWaitCount: 0,
            completedThisMonth: 0,
            hoursThisMonth: 0,
        };

        const thisMonth = format(startOfMonth(new Date()), 'yyyy-MM');
        // Volume and logged time per configured status — an admin may rename or
        // add statuses, so the catalogue drives the rows, not a literal list.
        const perStatus = new Map<string, { count: number; hours: number; state: LifecycleState }>();

        calculationRequests.forEach((request) => {
            const state = calculationStateOf(request);
            const stage = request.stage;
            const hours = Number(request.hours_spent ?? 0);

            // The finer stages only refine an open calculation; done and closed
            // come from the resolver so they survive a stale status catalogue.
            if (state === 'done') {
                summary.done += 1;
                summary.hoursDone += hours;
            } else if (state === 'cancelled') {
                summary.closed += 1;
            } else {
                summary.open += 1;
                if (stage === 'queued') summary.queued += 1;
                else if (stage === 'paused') summary.paused += 1;
                else summary.working += 1;
            }

            if (request.is_overdue) summary.overdue += 1;

            summary.hours += hours;

            const bucket = perStatus.get(request.status) ?? { count: 0, hours: 0, state };
            perStatus.set(request.status, { count: bucket.count + 1, hours: bucket.hours + hours, state });

            // Turnaround answers "in what time"; queue wait shows how long it sat
            // before they picked it up.
            if (request.turnaround_days !== null && request.turnaround_days !== undefined) {
                summary.turnaroundTotal += Number(request.turnaround_days);
                summary.turnaroundCount += 1;
            }

            if (request.queue_wait_days !== null && request.queue_wait_days !== undefined) {
                summary.queueWaitTotal += Number(request.queue_wait_days);
                summary.queueWaitCount += 1;
            }

            const completed = toDate(request.completed_at);
            if (state === 'done' && completed && format(completed, 'yyyy-MM') === thisMonth) {
                summary.completedThisMonth += 1;
                summary.hoursThisMonth += hours;
            }
        });

        const round = (value: number) => Math.round(value * 10) / 10;

        return {
            ...summary,
            hours: round(summary.hours),
            hoursDone: round(summary.hoursDone),
            hoursThisMonth: round(summary.hoursThisMonth),
            perStatus,
            averageTurnaround: summary.turnaroundCount > 0 ? round(summary.turnaroundTotal / summary.turnaroundCount) : null,
            averageQueueWait: summary.queueWaitCount > 0 ? round(summary.queueWaitTotal / summary.queueWaitCount) : null,
            /** Average time written per finished calculation. */
            averageHours: summary.done > 0 ? round(summary.hoursDone / summary.done) : null,
        };
    }, [calculationRequests]);

    const overdueTasks = useMemo(() => tasks.filter((task) => isOverdue(task.status, task.end_date)), [tasks]);
    const overdueWorkOrders = useMemo(
        () => workOrders.filter((workOrder) => isOverdue(workOrder.status, workOrder.estimated_date as string | undefined)),
        [workOrders],
    );
    const todaysWorkOrders = useMemo(() => workOrders.filter((workOrder) => isToday(workOrder.estimated_date as string | undefined)), [workOrders]);
    const completedThisWeek = useMemo(
        () =>
            workOrders.filter(
                (workOrder) =>
                    lifecycleOf(workOrder.status) === 'done' &&
                    isThisWeek((workOrder.completed_at as string | undefined) ?? (workOrder.updated_at as string | undefined)),
            ),
        [workOrders],
    );

    /** Deadlines landing in the current week — tasks and projects alike. */
    const dueThisWeek = useMemo(
        () =>
            [
                ...tasks.filter((task) => lifecycleOf(task.status) === 'open' && isThisWeek(task.end_date)),
                ...projects.filter((project) => lifecycleOf(project.status) === 'open' && isThisWeek(project.end_date)),
            ].length,
        [projects, tasks],
    );

    /**
     * Share of finished work orders that were closed on or before their
     * estimated date. Work orders without an estimate cannot be judged, so they
     * stay out of both sides of the ratio.
     */
    const slaOnTime = useMemo(() => {
        const judged = workOrders.filter(
            (workOrder) => lifecycleOf(workOrder.status) === 'done' && workOrder.estimated_date && (workOrder.completed_at || workOrder.updated_at),
        );

        if (judged.length === 0) {
            return null;
        }

        const onTime = judged.filter((workOrder) => {
            const estimated = toDate(workOrder.estimated_date as string | undefined);
            const closed = toDate((workOrder.completed_at as string | undefined) ?? (workOrder.updated_at as string | undefined));

            return estimated && closed && format(closed, 'yyyy-MM-dd') <= format(estimated, 'yyyy-MM-dd');
        }).length;

        return Math.round((onTime / judged.length) * 100);
    }, [workOrders]);

    /** Contacts come from the server; the per-company appends are the fallback. */
    const contactsCount = useMemo(
        () => stats.contacts || companies.reduce((sum, company) => sum + Number(company.total_people ?? 0), 0),
        [companies, stats.contacts],
    );

    const metrics = useMemo<MetricDefinition[]>(() => {
        const byCreation = <T extends { created_at?: string | null }>(records: T[]) => monthlyDelta(records, (record) => record.created_at);
        /** Sales is reported on the signing date, not on when the record was opened. */
        const bySigning = (records: Project[]) => monthlyDelta(records, (project) => project.signature_date);
        /** A service worker's month is measured from when work was assigned to them. */
        const byAssignment = (records: WorkOrder[]) => monthlyDelta(records, (workOrder) => assignedDateFor(workOrder, user.id));
        /** An engineer's month starts when the calculation reached their desk. */
        const byAcceptance = (records: CalculationRequest[]) => monthlyDelta(records, (request) => calculationDateOf(request));

        switch (roleView) {
            case 'sales':
                return [
                    {
                        key: 'leads',
                        icon: <TrendingUp className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.leads', 'Leads'),
                        value: activeLeads.length,
                        subtitle: t('employees.show.overview.metrics.leads_hint', 'Active leads'),
                    },
                    {
                        key: 'projects',
                        icon: <FolderOpen className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.projects', 'Projects'),
                        value: activeProjects.length,
                        subtitle: t('employees.show.overview.metrics.projects_hint', 'Active projects'),
                        // The count is live status; the trend is signing month over month.
                        delta: bySigning(projects),
                    },
                    {
                        key: 'companies',
                        icon: <Building2 className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.companies', 'Assigned companies'),
                        value: companies.length,
                        subtitle: t('employees.show.overview.metrics.companies_hint', 'Companies in their portfolio'),
                    },
                    {
                        key: 'contacts',
                        icon: <Contact className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.metrics.contacts', 'Contacts'),
                        value: contactsCount,
                        subtitle: t('employees.show.overview.metrics.contacts_hint', 'Contacts at those companies'),
                    },
                ];
            case 'engineer':
                return [
                    {
                        key: 'assigned_calculations',
                        icon: <Calculator className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.calculations_assigned', 'Assigned calculations'),
                        value: calculationStats.total,
                        subtitle: t('employees.show.overview.metrics.calculations_active_hint', ':count still open').replace(
                            ':count',
                            String(calculationStats.open),
                        ),
                        delta: byAcceptance(calculationRequests),
                    },
                    {
                        key: 'completed_calculations',
                        icon: <CalendarCheck className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.calculations_completed', 'Completed calculations'),
                        value: calculationStats.done,
                        subtitle: t('employees.show.overview.metrics.calculations_completed_hint', ':count this month').replace(
                            ':count',
                            String(calculationStats.completedThisMonth),
                        ),
                    },
                    {
                        key: 'calculation_hours',
                        icon: <Hourglass className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.calculations_hours', 'Hours logged'),
                        value: calculationStats.hours,
                        subtitle:
                            calculationStats.averageHours === null
                                ? t('employees.show.overview.metrics.calculations_hours_hint', 'Written on their calculations')
                                : t('employees.show.overview.metrics.calculations_hours_avg', ':hours h per completed calculation').replace(
                                      ':hours',
                                      String(calculationStats.averageHours),
                                  ),
                    },
                    {
                        key: 'calculation_turnaround',
                        icon: <CalendarClock className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.metrics.calculations_turnaround', 'Average turnaround'),
                        value:
                            calculationStats.averageTurnaround === null
                                ? '—'
                                : t('employees.show.overview.metrics.days', ':count d.').replace(
                                      ':count',
                                      String(calculationStats.averageTurnaround),
                                  ),
                        subtitle: t('employees.show.overview.metrics.calculations_overdue_hint', ':count overdue').replace(
                            ':count',
                            String(calculationStats.overdue),
                        ),
                    },
                ];
            case 'project_manager':
                return [
                    {
                        key: 'projects',
                        icon: <FolderOpen className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.projects', 'Projects'),
                        value: activeProjects.length,
                        subtitle: t('employees.show.overview.metrics.projects_hint', 'Active projects'),
                        delta: byCreation(projects),
                    },
                    {
                        key: 'tasks',
                        icon: <CheckSquare className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.tasks', 'Tasks'),
                        value: openTasks.length,
                        subtitle: t('employees.show.overview.metrics.open_tasks_hint', 'Open tasks'),
                        delta: byCreation(tasks),
                    },
                    {
                        key: 'team',
                        icon: <Users className="h-5 w-5" />,
                        tone: 'bg-info-subtle text-info-subtle-foreground',
                        label: t('employees.show.overview.metrics.team', 'Team members'),
                        value: stats.team_size,
                        subtitle: t('employees.show.overview.metrics.team_hint', 'Assigned on their projects'),
                    },
                    {
                        key: 'deadlines',
                        icon: <CalendarClock className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.metrics.deadlines', 'Deadlines this week'),
                        value: dueThisWeek,
                        subtitle: t('employees.show.overview.metrics.deadlines_hint', 'Due within this week'),
                    },
                ];
            case 'service_manager':
                return [
                    {
                        key: 'work_orders',
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                        subtitle: t('employees.show.overview.metrics.work_orders_hint', 'Currently open'),
                        delta: byCreation(workOrders),
                    },
                    {
                        key: 'tasks',
                        icon: <ClipboardList className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.active_tasks', 'Active tasks'),
                        value: openTasks.length,
                        subtitle: t('employees.show.overview.metrics.work_orders_hint', 'Currently open'),
                        delta: byCreation(tasks),
                    },
                    {
                        key: 'reports',
                        icon: <Users className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.service_employees', 'Service employees'),
                        value: reports.length,
                        subtitle: t('employees.show.overview.metrics.service_employees_hint', 'Direct reports'),
                    },
                    {
                        key: 'sla',
                        icon: <Gauge className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.metrics.sla', 'On time'),
                        value: slaOnTime === null ? '—' : `${slaOnTime}%`,
                        subtitle: t('employees.show.overview.metrics.sla_hint', 'Closed by the estimated date'),
                    },
                ];
            case 'service_worker':
                return [
                    {
                        key: 'work_orders',
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                        subtitle: t('employees.show.overview.metrics.assigned_hint', 'Assigned to them'),
                        delta: byAssignment(workOrders),
                    },
                    {
                        key: 'tasks',
                        icon: <ClipboardList className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.active_tasks', 'Active tasks'),
                        value: openTasks.length,
                        subtitle: t('employees.show.overview.metrics.assigned_hint', 'Assigned to them'),
                    },
                    {
                        key: 'today',
                        icon: <CalendarDays className="h-5 w-5" />,
                        tone: 'bg-info-subtle text-info-subtle-foreground',
                        label: t('employees.show.overview.metrics.today_visits', "Today's visits"),
                        value: todaysWorkOrders.length,
                        subtitle: t('employees.show.overview.metrics.today_visits_hint', 'Scheduled for today'),
                    },
                    {
                        key: 'completed',
                        icon: <CalendarCheck className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.completed_week', 'Completed this week'),
                        value: completedThisWeek.length,
                        subtitle: t('employees.show.overview.metrics.completed_week_hint', 'Work orders closed'),
                    },
                ];
            default:
                return [
                    {
                        key: 'companies',
                        icon: <Building2 className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.companies', 'Assigned companies'),
                        value: companies.length,
                        subtitle: t('employees.show.overview.metrics.companies_hint', 'Companies in their portfolio'),
                    },
                    {
                        key: 'projects',
                        icon: <FolderOpen className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.projects', 'Projects'),
                        value: activeProjects.length,
                        subtitle: t('employees.show.overview.metrics.projects_hint', 'Active projects'),
                        delta: byCreation(projects),
                    },
                    {
                        key: 'tasks',
                        icon: <CheckSquare className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.tasks', 'Tasks'),
                        value: openTasks.length,
                        subtitle: t('employees.show.overview.metrics.open_tasks_hint', 'Open tasks'),
                        delta: byCreation(tasks),
                    },
                    {
                        key: 'work_orders',
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                        subtitle: t('employees.show.overview.metrics.work_orders_hint', 'Currently open'),
                    },
                ];
        }
    }, [
        activeLeads.length,
        activeProjects.length,
        calculationRequests,
        calculationStats,
        companies.length,
        completedThisWeek.length,
        contactsCount,
        dueThisWeek,
        openTasks.length,
        openWorkOrders.length,
        projects,
        reports.length,
        roleView,
        slaOnTime,
        stats.team_size,
        t,
        tasks,
        todaysWorkOrders.length,
        user.id,
        workOrders,
    ]);

    /**
     * The trend panel plots how much work was *taken on* month by month, split
     * by where those records stand today, with a second series for the related
     * entity. Anything older than the selected window is dropped rather than
     * squeezed into the first bucket.
     */
    const trend = useMemo(() => {
        type TrendRecord = { date?: string | null; status?: string | null; amount?: number };

        /**
         * Each role reports on the date that means something to it: a sales
         * manager on when the deal was signed, a service worker on when the work
         * was assigned to them, everyone else on when the record was opened.
         */
        const bars: TrendRecord[] =
            roleView === 'engineer'
                ? calculationRequests.map((request) => ({
                      date: calculationDateOf(request),
                      // Resolved state, so a drifted status catalogue cannot flatten
                      // every calculation into one bar.
                      status:
                          calculationStateOf(request) === 'done' ? 'completed' : calculationStateOf(request) === 'cancelled' ? 'cancelled' : 'open',
                  }))
                : roleView === 'sales'
                  ? projects.map((project) => ({
                        date: project.signature_date,
                        status: project.status,
                        // Sales stacks the money on the deal, not the deal count.
                        amount: Number(project.sales_price ?? 0),
                    }))
                  : isService
                    ? workOrders.map((workOrder) => ({
                          date: roleView === 'service_worker' ? assignedDateFor(workOrder, user.id) : (workOrder.created_at as string | undefined),
                          status: workOrder.status,
                      }))
                    : projects.map((project) => ({ date: project.created_at as string | undefined, status: project.status }));

        /**
         * Sales gets a second column per period: leads beside the projects they
         * turn into, valued the same way. Leads carry no signing date, so they
         * are placed by when they were opened.
         */
        const secondaryBars: TrendRecord[] =
            roleView === 'sales'
                ? leads.map((lead) => ({
                      date: (lead as Lead & { created_at?: string | null }).created_at,
                      status: lead.status,
                      amount: Number(lead.estimated_value ?? lead.value ?? 0),
                  }))
                : roleView === 'project_manager'
                  ? // A project manager's second column is the task load behind the projects.
                    tasks.map((task) => ({
                        date: task.created_at as string | undefined,
                        status: task.status,
                    }))
                  : [];

        // The line carries the companion measure: how many deals sit behind the
        // money the sales bars stack, hours behind an engineer's calculations,
        // task volume everywhere else.
        const line: TrendRecord[] =
            roleView === 'sales'
                ? projects.map((project) => ({ date: project.signature_date, amount: 1 }))
                : roleView === 'project_manager'
                  ? []
                  : roleView === 'engineer'
                    ? calculationRequests.map((request) => ({
                          date: calculationDateOf(request),
                          amount: Number(request.hours_spent ?? 0),
                      }))
                    : tasks.map((task) => ({ date: task.created_at as string | undefined, amount: 1 }));

        const months = Number(trendMonths);
        const now = startOfMonth(new Date());
        const buckets = new Map<
            string,
            {
                label: string;
                open: number;
                done: number;
                cancelled: number;
                secondOpen: number;
                secondDone: number;
                secondCancelled: number;
                secondary: number;
            }
        >();

        const keyFor = (date: Date): string =>
            trendGrouping === 'quarter' ? `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}` : format(date, 'yyyy-MM');

        for (let index = months - 1; index >= 0; index -= 1) {
            const key = keyFor(subMonths(now, index));

            if (!buckets.has(key)) {
                buckets.set(key, {
                    label: key,
                    open: 0,
                    done: 0,
                    cancelled: 0,
                    secondOpen: 0,
                    secondDone: 0,
                    secondCancelled: 0,
                    secondary: 0,
                });
            }
        }

        const bucketFor = (value?: string | null) => {
            const date = toDate(value);

            return date ? buckets.get(keyFor(date)) : undefined;
        };

        bars.forEach((record) => {
            const bucket = bucketFor(record.date);
            if (bucket) {
                // Sales stacks money; every other view stacks record counts.
                bucket[lifecycleOf(record.status)] += record.amount ?? 1;
            }
        });

        const secondKeyFor = { open: 'secondOpen', done: 'secondDone', cancelled: 'secondCancelled' } as const;

        secondaryBars.forEach((record) => {
            const bucket = bucketFor(record.date);
            if (bucket) {
                bucket[secondKeyFor[lifecycleOf(record.status)]] += record.amount ?? 1;
            }
        });

        line.forEach((record) => {
            const bucket = bucketFor(record.date);
            if (bucket) {
                bucket.secondary += record.amount ?? 1;
            }
        });

        return Array.from(buckets.values());
    }, [calculationRequests, isService, leads, projects, roleView, tasks, trendGrouping, trendMonths, user.id, workOrders]);

    const trendLabels = useMemo(() => {
        switch (roleView) {
            case 'sales':
                return {
                    title: t('employees.show.overview.trend.sales_title', 'Sales overview'),
                    bars: t('employees.show.overview.trend.bars_signed_value', 'Value'),
                    secondary: t('employees.show.overview.trend.line_signed_count', 'Signed projects'),
                    description: t(
                        'employees.show.overview.trend.description_signing',
                        'Projects by signing date and leads by the month they were opened, valued and split by where they stand today.',
                    ),
                };
            case 'engineer':
                return {
                    title: t('employees.show.overview.trend.engineer_title', 'Calculations overview'),
                    bars: t('employees.show.overview.trend.bars_calculations', 'Calculations'),
                    secondary: t('employees.show.overview.trend.line_hours', 'Hours spent'),
                    description: t(
                        'employees.show.overview.trend.description_assigned',
                        'Calculations by the date they reached this engineer, split by where they stand today.',
                    ),
                };
            case 'project_manager':
                return {
                    title: t('employees.show.overview.trend.projects_title', 'Projects overview'),
                    bars: t('employees.show.overview.trend.bars_projects', 'Projects'),
                    secondary: t('employees.show.overview.trend.line_tasks', 'Tasks'),
                    description: t(
                        'employees.show.overview.trend.description_projects_tasks',
                        'Assigned projects and tasks per month, split by where they stand today.',
                    ),
                };
            case 'service_manager':
                return {
                    title: t('employees.show.overview.trend.service_title', 'Service team overview'),
                    bars: t('employees.show.overview.trend.bars_work_orders', 'Work orders'),
                    secondary: t('employees.show.overview.trend.line_tasks', 'Tasks'),
                    description: t(
                        'employees.show.overview.trend.description_created',
                        'Records created per period, split by where they stand today.',
                    ),
                };
            default:
                return {
                    title: t('employees.show.overview.trend.generic_title', 'Activity overview'),
                    bars: t('employees.show.overview.trend.bars_projects', 'Projects'),
                    secondary: t('employees.show.overview.trend.line_tasks', 'Tasks'),
                    description: t(
                        'employees.show.overview.trend.description_created',
                        'Records created per period, split by where they stand today.',
                    ),
                };
        }
    }, [roleView, t]);

    const trendConfig = useMemo(() => {
        const base = {
            open: {
                label: hasSecondStack
                    ? t('employees.show.overview.trend.projects_open', 'Projects – in progress')
                    : t('employees.show.overview.status.open', 'In progress'),
                color: 'color-mix(in srgb, var(--chart-1) 45%, var(--card))',
            },
            done: {
                label: hasSecondStack
                    ? t('employees.show.overview.trend.projects_done', 'Projects – completed')
                    : t('employees.show.overview.status.done', 'Completed'),
                color: 'color-mix(in srgb, var(--chart-6) 40%, var(--card))',
            },
            cancelled: {
                label: hasSecondStack
                    ? t('employees.show.overview.trend.projects_cancelled', 'Projects – cancelled')
                    : t('employees.show.overview.status.cancelled', 'Cancelled'),
                color: 'color-mix(in srgb, var(--chart-5) 40%, var(--card))',
            },
            secondary: { label: trendLabels.secondary, color: 'color-mix(in srgb, var(--chart-3) 80%, var(--card))' },
        };

        if (!hasSecondStack) {
            return base satisfies ChartConfig;
        }

        const secondLabels =
            roleView === 'sales'
                ? {
                      open: t('employees.show.overview.trend.leads_open', 'Leads – in progress'),
                      done: t('employees.show.overview.trend.leads_done', 'Leads – won'),
                      cancelled: t('employees.show.overview.trend.leads_cancelled', 'Leads – lost'),
                  }
                : {
                      open: t('employees.show.overview.trend.tasks_open', 'Tasks – in progress'),
                      done: t('employees.show.overview.trend.tasks_done', 'Tasks – completed'),
                      cancelled: t('employees.show.overview.trend.tasks_cancelled', 'Tasks – cancelled'),
                  };

        // A second hue family keeps the two columns apart at a glance.
        return {
            ...base,
            secondOpen: {
                label: secondLabels.open,
                color: 'color-mix(in srgb, var(--chart-3) 45%, var(--card))',
            },
            secondDone: {
                label: secondLabels.done,
                color: 'color-mix(in srgb, var(--chart-2) 45%, var(--card))',
            },
            secondCancelled: {
                label: secondLabels.cancelled,
                color: 'color-mix(in srgb, var(--chart-4) 40%, var(--card))',
            },
        } satisfies ChartConfig;
    }, [hasSecondStack, roleView, t, trendLabels.secondary]);

    const statusGroups = useMemo<StatusGroup[]>(() => {
        const summarise = (records: Array<{ status?: string | null }>) =>
            records.reduce(
                (acc, record) => {
                    acc[lifecycleOf(record.status)] += 1;

                    return acc;
                },
                { open: 0, done: 0, cancelled: 0 },
            );

        const lifecycleItems = (records: Array<{ status?: string | null }>) => {
            const summary = summarise(records);

            return [
                { label: t('employees.show.overview.status.open', 'In progress'), count: summary.open, dot: 'bg-primary' },
                { label: t('employees.show.overview.status.done', 'Completed'), count: summary.done, dot: 'bg-success' },
                { label: t('employees.show.overview.status.cancelled', 'Cancelled'), count: summary.cancelled, dot: 'bg-destructive' },
            ];
        };

        const workItems = (records: Array<{ status?: string | null }>, overdue: number) => {
            const summary = summarise(records);

            return [
                { label: t('employees.show.overview.status.open', 'In progress'), count: summary.open, dot: 'bg-primary' },
                { label: t('employees.show.overview.status.done', 'Completed'), count: summary.done, dot: 'bg-success' },
                { label: t('employees.show.overview.status.overdue', 'Overdue'), count: overdue, dot: 'bg-destructive' },
                { label: t('employees.show.overview.status.cancelled', 'Cancelled'), count: summary.cancelled, dot: 'bg-muted-foreground' },
            ];
        };

        if (roleView === 'engineer') {
            const stateDot: Record<LifecycleState, string> = {
                open: 'bg-primary',
                done: 'bg-success',
                cancelled: 'bg-destructive',
            };
            // settings.* first: the catalogue labels the server sends come from
            // config.json and are English only.
            const catalogue = new Map(
                calculationStatuses.map((option) => [option.value, settingLabel(t, 'calculation_request_statuses', option.value) || option.label]),
            );

            // Built from the statuses the calculations actually carry, so nothing
            // disappears when the settings catalogue no longer lists them. The
            // catalogue is used for labels only.
            const statusItems = Array.from(calculationStats.perStatus.entries())
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([value, bucket]) => ({
                    label: catalogue.get(value) ?? settingLabel(t, 'calculation_request_statuses', value) ?? humaniseStatus(value),
                    count: bucket.count,
                    hint:
                        bucket.hours > 0
                            ? t('employees.show.overview.status.hours', ':count h').replace(':count', String(Math.round(bucket.hours * 10) / 10))
                            : undefined,
                    dot: stateDot[bucket.state],
                }));

            const groups: StatusGroup[] = [];

            if (statusItems.length > 0) {
                groups.push({ title: t('employees.show.overview.groups.calculations', 'Calculations'), items: statusItems });
            }

            groups.push({
                title: t('employees.show.overview.groups.time', 'Time'),
                items: [
                    {
                        label: t('employees.show.overview.metrics.calculations_hours', 'Hours logged'),
                        count: calculationStats.hours,
                        dot: 'bg-cat-purple-foreground',
                    },
                    {
                        label: t('employees.show.overview.status.avg_turnaround', 'Avg. turnaround'),
                        count: calculationStats.averageTurnaround ?? 0,
                        hint: t('employees.show.overview.status.days_unit', 'days'),
                        dot: 'bg-primary',
                    },
                    {
                        label: t('employees.show.overview.status.avg_queue_wait', 'Avg. wait in queue'),
                        count: calculationStats.averageQueueWait ?? 0,
                        hint: t('employees.show.overview.status.days_unit', 'days'),
                        dot: 'bg-info',
                    },
                    {
                        label: t('employees.show.overview.status.overdue', 'Overdue'),
                        count: calculationStats.overdue,
                        dot: 'bg-destructive',
                    },
                ],
            });

            if (tasks.length > 0) {
                groups.push({ title: t('employees.show.overview.groups.tasks', 'Tasks'), items: workItems(tasks, overdueTasks.length) });
            }

            return groups;
        }

        if (isService) {
            return [
                { title: t('employees.show.overview.groups.work_orders', 'Work orders'), items: workItems(workOrders, overdueWorkOrders.length) },
                { title: t('employees.show.overview.groups.tasks', 'Tasks'), items: workItems(tasks, overdueTasks.length) },
            ];
        }

        if (roleView === 'sales') {
            return [
                { title: t('employees.show.overview.groups.leads', 'Leads'), items: lifecycleItems(leads) },
                { title: t('employees.show.overview.groups.projects', 'Projects'), items: lifecycleItems(projects) },
            ];
        }

        return [
            { title: t('employees.show.overview.groups.projects', 'Projects'), items: lifecycleItems(projects) },
            { title: t('employees.show.overview.groups.tasks', 'Tasks'), items: workItems(tasks, overdueTasks.length) },
        ];
    }, [
        calculationStatuses,
        calculationStats,
        isService,
        leads,
        overdueTasks.length,
        overdueWorkOrders.length,
        projects,
        roleView,
        t,
        tasks,
        workOrders,
    ]);

    /** The rail's two role-specific figures, above the period and joined date. */
    const summaryRows = useMemo(() => {
        switch (roleView) {
            case 'sales':
                return [
                    {
                        icon: <Building2 className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.companies', 'Assigned companies'),
                        value: companies.length,
                    },
                    {
                        icon: <TrendingUp className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.active_leads', 'Active leads'),
                        value: activeLeads.length,
                    },
                    {
                        icon: <FolderOpen className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.active_projects', 'Active projects'),
                        value: activeProjects.length,
                    },
                ];
            case 'project_manager':
                return [
                    {
                        icon: <FolderOpen className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.managed_projects', 'Managed projects'),
                        value: activeProjects.length,
                    },
                    {
                        icon: <CheckSquare className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.tasks', 'Tasks'),
                        value: openTasks.length,
                    },
                ];
            case 'engineer':
                return [
                    {
                        icon: <Calculator className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.active_calculations', 'Active calculations'),
                        value: calculationStats.open,
                    },
                    {
                        icon: <CalendarCheck className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.calculations_completed', 'Completed calculations'),
                        value: calculationStats.done,
                    },
                    {
                        icon: <Hourglass className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.hours_spent', 'Hours logged'),
                        value: calculationStats.hours,
                    },
                ];
            case 'service_manager':
                return [
                    {
                        icon: <Users className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.managed_employees', 'Managed employees'),
                        value: reports.length,
                    },
                    {
                        icon: <Wrench className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                    },
                ];
            case 'service_worker':
                return [
                    {
                        icon: <Wrench className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                    },
                    {
                        icon: <CalendarDays className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.today_visits', "Today's visits"),
                        value: todaysWorkOrders.length,
                    },
                ];
            default:
                return [
                    {
                        icon: <FolderOpen className="h-4 w-4" />,
                        label: t('employees.show.overview.summary.managed_projects', 'Managed projects'),
                        value: activeProjects.length,
                    },
                    {
                        icon: <CheckSquare className="h-4 w-4" />,
                        label: t('employees.show.overview.metrics.tasks', 'Tasks'),
                        value: openTasks.length,
                    },
                ];
        }
    }, [
        activeLeads.length,
        activeProjects.length,
        calculationStats,
        companies.length,
        openTasks.length,
        openWorkOrders.length,
        reports.length,
        roleView,
        t,
        todaysWorkOrders.length,
    ]);

    /** The bottom strip — four counts that never change with the period filter. */
    const workSummary = useMemo(() => {
        switch (roleView) {
            case 'sales':
                return [
                    {
                        icon: <Building2 className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.companies', 'Assigned companies'),
                        value: companies.length,
                    },
                    {
                        icon: <Contact className="h-5 w-5" />,
                        tone: 'bg-info-subtle text-info-subtle-foreground',
                        label: t('employees.show.overview.metrics.contacts', 'Contacts'),
                        value: contactsCount,
                    },
                    {
                        icon: <TrendingUp className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.summary.owned_leads', 'Owned leads'),
                        value: leads.length,
                    },
                    {
                        icon: <FolderOpen className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.summary.managed_projects', 'Managed projects'),
                        value: projects.length,
                    },
                ];
            case 'engineer':
                return [
                    {
                        icon: <Calculator className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.summary.total_calculations', 'Calculations assigned'),
                        value: calculationStats.total,
                    },
                    {
                        icon: <CalendarCheck className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.calculations_completed', 'Completed calculations'),
                        value: calculationStats.done,
                    },
                    {
                        icon: <Hourglass className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.summary.hours_spent', 'Hours logged'),
                        value: calculationStats.hours,
                    },
                    {
                        icon: <CalendarClock className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.status.avg_turnaround', 'Avg. turnaround'),
                        value:
                            calculationStats.averageTurnaround === null
                                ? '—'
                                : t('employees.show.overview.metrics.days', ':count d.').replace(
                                      ':count',
                                      String(calculationStats.averageTurnaround),
                                  ),
                    },
                ];
            case 'service_manager':
                return [
                    {
                        icon: <Users className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.summary.managed_employees', 'Managed employees'),
                        value: reports.length,
                    },
                    {
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                    },
                    {
                        icon: <ClipboardList className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.metrics.active_tasks', 'Active tasks'),
                        value: openTasks.length,
                    },
                    {
                        icon: <CalendarClock className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.status.overdue', 'Overdue'),
                        value: overdueWorkOrders.length + overdueTasks.length,
                    },
                ];
            case 'service_worker':
                return [
                    {
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                    },
                    {
                        icon: <CalendarDays className="h-5 w-5" />,
                        tone: 'bg-info-subtle text-info-subtle-foreground',
                        label: t('employees.show.overview.metrics.today_visits', "Today's visits"),
                        value: todaysWorkOrders.length,
                    },
                    {
                        icon: <CalendarCheck className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.completed_week', 'Completed this week'),
                        value: completedThisWeek.length,
                    },
                    {
                        icon: <CalendarClock className="h-5 w-5" />,
                        tone: 'bg-warning-subtle text-warning-subtle-foreground',
                        label: t('employees.show.overview.status.overdue', 'Overdue'),
                        value: overdueWorkOrders.length + overdueTasks.length,
                    },
                ];
            default:
                return [
                    {
                        icon: <FolderOpen className="h-5 w-5" />,
                        tone: 'bg-cat-purple text-cat-purple-foreground',
                        label: t('employees.show.overview.summary.managed_projects', 'Managed projects'),
                        value: projects.length,
                    },
                    {
                        icon: <CheckSquare className="h-5 w-5" />,
                        tone: 'bg-success-subtle text-success-subtle-foreground',
                        label: t('employees.show.overview.metrics.active_tasks', 'Active tasks'),
                        value: openTasks.length,
                    },
                    {
                        icon: <Users className="h-5 w-5" />,
                        tone: 'bg-info-subtle text-info-subtle-foreground',
                        label: t('employees.show.overview.metrics.team', 'Team members'),
                        value: stats.team_size,
                    },
                    {
                        icon: <Wrench className="h-5 w-5" />,
                        tone: 'bg-primary/12 text-primary',
                        label: t('employees.show.overview.metrics.work_orders', 'Active work orders'),
                        value: openWorkOrders.length,
                    },
                ];
        }
    }, [
        calculationStats,
        companies.length,
        completedThisWeek.length,
        contactsCount,
        leads.length,
        openTasks.length,
        openWorkOrders.length,
        overdueTasks.length,
        overdueWorkOrders.length,
        projects,
        reports.length,
        roleView,
        stats.team_size,
        t,
        todaysWorkOrders.length,
    ]);

    const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
    const currentPeriod = `${format(new Date(), 'yyyy')} Q${Math.floor(new Date().getMonth() / 3) + 1}`;
    const managerName = manager ? `${manager.first_name ?? ''} ${manager.last_name ?? ''}`.trim() : null;

    const roleNote =
        roleView === 'engineer'
            ? t(
                  'employees.show.overview.note.engineer',
                  'Figures cover the calculations assigned to this engineer, counted from the date each one reached them.',
              )
            : roleView === 'service_worker'
              ? t(
                    'employees.show.overview.note.service_worker',
                    'This view focuses on work assigned to this employee. See every record under the "Work orders" tab.',
                )
              : roleView === 'service_manager'
                ? t(
                      'employees.show.overview.note.service_manager',
                      'Figures cover the service work this employee is responsible for and the colleagues reporting to them.',
                  )
                : t(
                      'employees.show.overview.note.default',
                      'Figures are tailored to this employee’s role — only the records assigned to them are counted.',
                  );

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {metrics.map((metric) => (
                    <MetricCard {...metric} key={metric.key} deltaLabel={t('employees.show.overview.vs_last_month', 'vs last month')} />
                ))}
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4 xl:items-start">
                <div className="space-y-4 xl:col-span-3">
                    {roleView === 'service_worker' ? (
                        <CurrentWorkCard workOrders={workOrders} />
                    ) : (
                        <Card>
                            <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="space-y-1">
                                    <CardTitle>{trendLabels.title}</CardTitle>
                                    <CardDescription>{trendLabels.description}</CardDescription>
                                </div>
                                <div className="flex shrink-0 flex-wrap gap-2">
                                    <Select value={trendMonths} onValueChange={(value) => setTrendMonths(value as '6' | '12')}>
                                        <SelectTrigger className="h-9 w-[170px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="6">{t('employees.show.overview.trend.last_6', 'Last 6 months')}</SelectItem>
                                            <SelectItem value="12">{t('employees.show.overview.trend.last_12', 'Last 12 months')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select value={trendGrouping} onValueChange={(value) => setTrendGrouping(value as 'month' | 'quarter')}>
                                        <SelectTrigger className="h-9 w-[140px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="month">{t('employees.show.overview.trend.by_month', 'By month')}</SelectItem>
                                            <SelectItem value="quarter">{t('employees.show.overview.trend.by_quarter', 'By quarter')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <ChartContainer config={trendConfig} className={isMobile ? 'h-[220px] w-full' : 'h-[300px] w-full'}>
                                    <ComposedChart
                                        data={trend}
                                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                                        onMouseLeave={() => setHoveredStack(null)}
                                    >
                                        <CartesianGrid strokeDasharray="4 4" vertical={false} className="stroke-muted opacity-35" />
                                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                                        <YAxis
                                            yAxisId="left"
                                            allowDecimals={false}
                                            tickLine={false}
                                            axisLine={false}
                                            width={roleView === 'sales' ? 52 : 32}
                                            tickFormatter={(value: number) => (roleView === 'sales' ? compactNumber(value) : String(value))}
                                        />
                                        {hasLineSeries && (
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                allowDecimals={false}
                                                tickLine={false}
                                                axisLine={false}
                                                width={32}
                                            />
                                        )}
                                        <ChartTooltip
                                            cursor={{ fill: 'var(--muted)', opacity: 0.35 }}
                                            content={
                                                <TrendTooltip
                                                    config={trendConfig}
                                                    stack={hoveredStack}
                                                    isMoney={roleView === 'sales'}
                                                    totalLabel={t('employees.show.overview.trend.total', 'Total')}
                                                />
                                            }
                                        />
                                        <ChartLegend content={<ChartLegendContent />} />
                                        <Bar
                                            yAxisId="left"
                                            dataKey="open"
                                            stackId="lifecycle"
                                            fill="var(--color-open)"
                                            shape={stackTopRadius(LIFECYCLE_KEYS, 'open')}
                                            onMouseOver={() => setHoveredStack(hasSecondStack ? 'primary' : null)}
                                        />
                                        <Bar
                                            yAxisId="left"
                                            dataKey="done"
                                            stackId="lifecycle"
                                            fill="var(--color-done)"
                                            shape={stackTopRadius(LIFECYCLE_KEYS, 'done')}
                                            onMouseOver={() => setHoveredStack(hasSecondStack ? 'primary' : null)}
                                        />
                                        <Bar
                                            yAxisId="left"
                                            dataKey="cancelled"
                                            stackId="lifecycle"
                                            fill="var(--color-cancelled)"
                                            shape={stackTopRadius(LIFECYCLE_KEYS, 'cancelled')}
                                            onMouseOver={() => setHoveredStack(hasSecondStack ? 'primary' : null)}
                                        />
                                        {/* A separate stackId puts leads in their own column beside the projects. */}
                                        {hasSecondStack && (
                                            <Bar
                                                yAxisId="left"
                                                dataKey="secondOpen"
                                                stackId="leads"
                                                fill="var(--color-secondOpen)"
                                                shape={stackTopRadius(SECOND_STACK_KEYS, 'secondOpen')}
                                                onMouseOver={() => setHoveredStack('second')}
                                            />
                                        )}
                                        {hasSecondStack && (
                                            <Bar
                                                yAxisId="left"
                                                dataKey="secondDone"
                                                stackId="leads"
                                                fill="var(--color-secondDone)"
                                                shape={stackTopRadius(SECOND_STACK_KEYS, 'secondDone')}
                                                onMouseOver={() => setHoveredStack('second')}
                                            />
                                        )}
                                        {hasSecondStack && (
                                            <Bar
                                                yAxisId="left"
                                                dataKey="secondCancelled"
                                                stackId="leads"
                                                fill="var(--color-secondCancelled)"
                                                shape={stackTopRadius(SECOND_STACK_KEYS, 'secondCancelled')}
                                                onMouseOver={() => setHoveredStack('second')}
                                            />
                                        )}
                                        {hasLineSeries && (
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="secondary"
                                                stroke="var(--color-secondary)"
                                                strokeWidth={2}
                                                dot={{ r: 3 }}
                                            />
                                        )}
                                    </ComposedChart>
                                </ChartContainer>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle>{t('employees.show.overview.work_summary', 'Work summary')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 divide-y rounded-lg border sm:grid-cols-2 sm:divide-x xl:grid-cols-4 xl:divide-y-0">
                                {workSummary.map((item) => (
                                    <div key={item.label} className="flex items-center gap-3 p-4">
                                        <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', item.tone)}>
                                            {item.icon}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-muted-foreground truncate text-xs font-medium">{item.label}</div>
                                            <div className="mt-0.5 text-lg font-semibold tabular-nums">{item.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-info-subtle text-info-subtle-foreground flex items-start gap-2.5 rounded-lg p-3 text-sm">
                                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                                <p>{roleNote}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-4 xl:col-span-1">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle>{t('employees.show.overview.employee_summary', 'Employee summary')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-start gap-3">
                                <span className="bg-primary/12 text-primary inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold">
                                    {initials || <UserIcon className="h-5 w-5" />}
                                </span>
                                <div className="min-w-0 space-y-1">
                                    <div className="flex min-w-0 items-center gap-1.5">
                                        <span className="truncate font-semibold">
                                            {user.first_name} {user.last_name}
                                        </span>
                                        {user.azure_id ? (
                                            <span
                                                aria-label="Microsoft 365"
                                                title={t('employees.show.m365_badge', 'Microsoft 365 user')}
                                                className="inline-flex shrink-0"
                                            >
                                                <svg width="13" height="13" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                                                    <path fill="#F25022" d="M1 1h10v10H1z" />
                                                    <path fill="#7FBA00" d="M12 1h10v10H12z" />
                                                    <path fill="#00A4EF" d="M1 12h10v10H1z" />
                                                    <path fill="#FFB900" d="M12 12h10v10H12z" />
                                                </svg>
                                            </span>
                                        ) : null}
                                    </div>
                                    {user.position && <div className="text-muted-foreground truncate text-sm">{user.position}</div>}
                                    {/* Role and department read as tags rather than two more labelled rows. */}
                                    <div className="flex flex-wrap gap-1">
                                        {user.role && <Badge variant="secondary">{String(user.role)}</Badge>}
                                        {user.department && <Badge variant="outline">{String(user.department)}</Badge>}
                                    </div>
                                </div>
                            </div>

                            {/* Contact details live here now — one line each, no second card. */}
                            <div className="space-y-1.5 border-t pt-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                                    {user.email ? (
                                        <a href={`mailto:${user.email}`} className="text-primary min-w-0 truncate hover:underline">
                                            {user.email}
                                        </a>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone className="text-muted-foreground h-4 w-4 shrink-0" />
                                    {user.phone ? (
                                        <a href={`tel:${user.phone}`} className="text-primary min-w-0 truncate hover:underline">
                                            {user.phone}
                                        </a>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </div>
                                {managerName && (
                                    <div className="flex items-center gap-2">
                                        <UserRound className="text-muted-foreground h-4 w-4 shrink-0" />
                                        {manager?.id ? (
                                            <Link href={`/employees/${manager.id}`} className="text-primary min-w-0 truncate hover:underline">
                                                {managerName}
                                            </Link>
                                        ) : (
                                            <span className="min-w-0 truncate">{managerName}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="divide-y border-t pt-1">
                                {summaryRows.map((row) => (
                                    <SummaryRow key={row.label} icon={row.icon} label={row.label}>
                                        {row.value}
                                    </SummaryRow>
                                ))}
                                <SummaryRow
                                    icon={<CalendarDays className="h-4 w-4" />}
                                    label={t('employees.show.overview.current_period', 'Current period')}
                                >
                                    {currentPeriod}
                                </SummaryRow>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle>{t('employees.show.overview.status_summary', 'Status summary')}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {statusGroups.map((group) => (
                                <div key={group.title} className="rounded-lg border p-3">
                                    <p className="text-primary mb-2 text-sm font-semibold">{group.title}</p>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-2">
                                        {group.items.map((item) => (
                                            <div key={item.label} className="min-w-0">
                                                <p className="text-muted-foreground truncate text-xs">{item.label}</p>
                                                <p className="mt-0.5 flex items-baseline gap-1.5 text-sm font-semibold tabular-nums">
                                                    <span className={cn('inline-block h-2 w-2 shrink-0 translate-y-[-1px] rounded-full', item.dot)} />
                                                    {item.count}
                                                    {item.hint && <span className="text-muted-foreground text-xs font-normal">{item.hint}</span>}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {reports.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle>{t('employees.show.overview.direct_reports', 'Direct reports')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {reports.slice(0, 6).map((report) => (
                                    <Link
                                        key={report.id}
                                        href={`/employees/${report.id}`}
                                        className="hover:bg-accent flex items-center gap-2.5 rounded-lg p-2 transition-colors"
                                    >
                                        <span className="bg-secondary text-secondary-foreground inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                                            {`${report.first_name?.[0] ?? ''}${report.last_name?.[0] ?? ''}`.toUpperCase()}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-medium">
                                                {report.first_name} {report.last_name}
                                            </span>
                                            {(report.position || report.role) && (
                                                <span className="text-muted-foreground block truncate text-xs">{report.position || report.role}</span>
                                            )}
                                        </span>
                                    </Link>
                                ))}
                                {reports.length > 6 && (
                                    <p className="text-muted-foreground px-2 text-xs">
                                        {t('employees.show.overview.more_reports', '+:count more').replace(':count', String(reports.length - 6))}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * The jobs board's tabs, in its order. Kept identical to
 * `components/features/WorkOrders/components/JobsList.tsx` so the profile and the
 * mobile calendar answer "what is on this worker's plate" the same way.
 */
type WorkFilter = 'today' | 'overdue' | 'unscheduled' | 'completed' | 'all';

/** The four statuses that close a job — mirrors WorkOrder::TERMINAL_STATUSES. */
const TERMINAL_WORK_ORDER_STATUSES = ['completed', 'canceled', 'cancelled', 'closed'];

const isTerminalWorkOrder = (status?: string | null): boolean => TERMINAL_WORK_ORDER_STATUSES.includes(String(status ?? ''));

/** Midnight-normalised, so a date compares as a day rather than an instant. */
const startOfDay = (value: Date): Date => new Date(value.getFullYear(), value.getMonth(), value.getDate());

interface WorkItem {
    key: string;
    title: string;
    subtitle: string;
    client: string;
    priority?: string | null;
    due?: string | null;
    status?: string | null;
    location: string;
    href: string;
}

/**
 * The service worker's board, showing the jobs their mobile calendar shows:
 * the same tabs, the same day semantics (today carries the overdue backlog),
 * the same overdue-first ordering, and terminal jobs kept out of every tab but
 * "Completed". The payload is already narrowed server-side by
 * `WorkOrder::scopeJobsBoard()`, so this filters the same set the calendar has.
 *
 * Project tasks are deliberately absent — they are not part of the service
 * flow; the Tasks tab still lists them in full.
 */
function CurrentWorkCard({ workOrders }: { workOrders: WorkOrder[] }) {
    const { t } = useTranslations();
    const { settings } = useSettings();

    const [filter, setFilter] = useState<WorkFilter>('today');
    const [search, setSearch] = useState('');

    const items = useMemo<WorkItem[]>(() => {
        const priorityLabel = (value?: string | null) =>
            settings.workOrderPriorities.find((option) => option.value === value)?.label ?? value ?? null;

        return workOrders.map((workOrder) => ({
            key: `work-order-${workOrderIdOf(workOrder)}`,
            title: (workOrder.description || '').trim() || workOrder.wo_number || t('employees.show.overview.work.untitled', 'Work order'),
            subtitle: workOrder.wo_number
                ? t('employees.show.overview.work.order_number', 'Order :number').replace(':number', String(workOrder.wo_number))
                : t('employees.show.overview.work.work_order', 'Work order'),
            client: workOrder.company?.name || `${workOrder.contact_first_name || ''} ${workOrder.contact_last_name || ''}`.trim() || '',
            priority: priorityLabel(workOrder.priority),
            due: (workOrder.estimated_date as string | undefined) ?? null,
            status: workOrder.status,
            location: workOrder.service_address || '',
            href: `/work-orders/${workOrderIdOf(workOrder)}`,
        }));
    }, [settings.workOrderPriorities, t, workOrders]);

    /** The jobs board's rules, applied to one item. */
    const matchesFilter = (item: WorkItem, which: WorkFilter): boolean => {
        const terminal = isTerminalWorkOrder(item.status);

        if (which === 'completed') {
            return terminal;
        }

        if (terminal) {
            return false;
        }

        const due = toDate(item.due);
        const today = startOfDay(new Date());

        switch (which) {
            case 'today':
                // Today's jobs plus the overdue backlog, exactly as the calendar
                // shows them when the selected day is today.
                return Boolean(due) && startOfDay(due as Date) <= today;
            case 'overdue':
                return Boolean(due) && startOfDay(due as Date) < today;
            case 'unscheduled':
                return !item.due;
            default:
                return true;
        }
    };

    const counts = useMemo(
        () => ({
            today: items.filter((item) => matchesFilter(item, 'today')).length,
            overdue: items.filter((item) => matchesFilter(item, 'overdue')).length,
            unscheduled: items.filter((item) => matchesFilter(item, 'unscheduled')).length,
            completed: items.filter((item) => matchesFilter(item, 'completed')).length,
            all: items.filter((item) => matchesFilter(item, 'all')).length,
        }),
        [items],
    );

    const visibleItems = useMemo(() => {
        const needle = search.trim().toLowerCase();
        const today = startOfDay(new Date());

        return items
            .filter((item) => {
                if (!matchesFilter(item, filter)) {
                    return false;
                }

                if (!needle) {
                    return true;
                }

                return [item.title, item.subtitle, item.client, item.location].join(' ').toLowerCase().includes(needle);
            })
            .sort((a, b) => {
                // Overdue first, then by due date, undated last — the board's order.
                const aDue = toDate(a.due);
                const bDue = toDate(b.due);
                const aOverdue = Boolean(aDue && startOfDay(aDue) < today);
                const bOverdue = Boolean(bDue && startOfDay(bDue) < today);

                if (aOverdue !== bOverdue) {
                    return aOverdue ? -1 : 1;
                }

                if (aDue && bDue) {
                    return aDue.getTime() - bDue.getTime();
                }

                if (!aDue && bDue) {
                    return 1;
                }

                if (aDue && !bDue) {
                    return -1;
                }

                return 0;
            });
    }, [filter, items, search]);

    const statusTone = (item: WorkItem) => {
        if (isOverdue(item.status, item.due)) {
            return 'bg-danger-subtle text-danger-subtle-foreground';
        }

        const state = lifecycleOf(item.status);
        if (state === 'done') {
            return 'bg-success-subtle text-success-subtle-foreground';
        }
        if (state === 'cancelled') {
            return 'bg-muted text-muted-foreground';
        }

        return 'bg-info-subtle text-info-subtle-foreground';
    };

    const statusText = (item: WorkItem) => settings.workOrderStatuses.find((option) => option.value === item.status)?.label ?? item.status ?? '—';

    const filters: Array<{ value: WorkFilter; label: string; count: number }> = [
        { value: 'today', label: t('employees.show.overview.work.filter_today', 'Today'), count: counts.today },
        { value: 'overdue', label: t('employees.show.overview.status.overdue', 'Overdue'), count: counts.overdue },
        { value: 'unscheduled', label: t('employees.show.overview.work.filter_unscheduled', 'Unscheduled'), count: counts.unscheduled },
        { value: 'completed', label: t('employees.show.overview.status.done', 'Completed'), count: counts.completed },
        { value: 'all', label: t('employees.show.overview.work.filter_all', 'All'), count: counts.all },
    ];

    return (
        <Card>
            <CardHeader className="flex flex-col gap-3 pb-2">
                <div className="space-y-1">
                    <CardTitle>{t('employees.show.overview.work.title', 'Current work')}</CardTitle>
                    <CardDescription>
                        {t('employees.show.overview.work.description', 'The same jobs this worker sees on their mobile calendar.')}
                    </CardDescription>
                </div>
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-wrap gap-2">
                        {filters.map((option) => (
                            <Button
                                key={option.value}
                                type="button"
                                size="sm"
                                variant={filter === option.value ? 'default' : 'outline'}
                                className="gap-1.5"
                                onClick={() => setFilter(option.value)}
                            >
                                {option.label}
                                <span className="text-xs opacity-80">{option.count}</span>
                            </Button>
                        ))}
                    </div>
                    <div className="relative lg:w-72">
                        <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t('employees.show.overview.work.search', 'Search work orders…')}
                            className="pl-8"
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<ClipboardList />}
                        title={t('employees.show.overview.work.empty_title', 'Nothing assigned')}
                        description={t('employees.show.overview.work.empty_description', 'No work orders match this filter.')}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('employees.show.overview.work.column_item', 'Work order')}</TableHead>
                                    <TableHead>{t('employees.show.overview.work.column_client', 'Client / company')}</TableHead>
                                    <TableHead>{t('employees.show.overview.work.column_priority', 'Priority')}</TableHead>
                                    <TableHead>{t('employees.show.overview.work.column_due', 'Scheduled')}</TableHead>
                                    <TableHead>{t('employees.show.overview.work.column_status', 'Status')}</TableHead>
                                    <TableHead>{t('employees.show.overview.work.column_location', 'Location')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visibleItems.slice(0, 10).map((item) => (
                                    <TableRow key={item.key}>
                                        <TableCell className="max-w-[260px]">
                                            <Link href={item.href} className="text-primary block truncate font-medium hover:underline">
                                                {item.title}
                                            </Link>
                                            <span className="text-muted-foreground block truncate text-xs">{item.subtitle}</span>
                                        </TableCell>
                                        <TableCell className="max-w-[180px] truncate">
                                            {item.client || <span className="text-muted-foreground">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            {item.priority ? (
                                                <Badge variant="outline">{item.priority}</Badge>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell className={cn('whitespace-nowrap', isOverdue(item.status, item.due) && 'text-destructive')}>
                                            {item.due ? (
                                                format(new Date(item.due), 'yyyy.MM.dd')
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    {t('employees.show.overview.work.filter_unscheduled', 'Unscheduled')}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={cn('border-transparent', statusTone(item))}>
                                                {statusText(item)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="max-w-[200px]">
                                            {item.location ? (
                                                <span className="flex items-center gap-1.5 truncate">
                                                    <MapPin className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                    <span className="truncate">{item.location}</span>
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                <Link href="/work-orders" className="text-primary inline-flex items-center gap-1 text-sm hover:underline">
                    {t('employees.show.overview.work.view_all', 'View all work orders')}
                    <span aria-hidden>→</span>
                </Link>
            </CardContent>
        </Card>
    );
}
